import React, { useState, useEffect } from 'react';
import './UsernameCreation.css';
import { authAPI } from '../utils/apiUtils';
import avatarAssets from '../utils/avatarAssets';

const UsernameCreation = ({ isOpen, onClose, onComplete, userInfo }) => {
  const [username, setUsername] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Pre-fill username when component opens if user already has one
  React.useEffect(() => {
    if (isOpen && userInfo && userInfo.username) {
      setUsername(userInfo.username);
      console.log('Pre-filling username from user info:', userInfo.username);
    } else if (!isOpen) {
      // Reset username when modal closes
      setUsername('');
    }
  }, [isOpen, userInfo]);

  // Mobile-specific optimizations
  useEffect(() => {
    if (isOpen) {
      // Prevent body scroll on mobile when modal is open
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      
      // Focus the input on mobile for better UX
      const input = document.getElementById('username');
      if (input) {
        // Small delay to ensure the modal is fully rendered
        setTimeout(() => {
          input.focus();
          // For mobile, scroll to input if needed
          if (window.innerWidth <= 768) {
            input.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
      }
    } else {
      // Restore body scroll when modal closes
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    }

    // Cleanup function
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    };
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!username.trim()) {
      setError('Please enter a username');
      return;
    }

    if (username.length < 3) {
      setError('Username must be at least 3 characters long');
      return;
    }

    if (username.length > 20) {
      setError('Username must be less than 20 characters');
      return;
    }

    // Basic validation for username format
    const usernameRegex = /^[a-zA-Z0-9_-]+$/;
    if (!usernameRegex.test(username)) {
      setError('Username can only contain letters, numbers, hyphens, and underscores');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // Call the backend API to update the username
      const response = await authAPI.updateProfile({
        username: username.trim()
      });
      
      // Complete the username creation process
      const updatedUserInfo = {
        ...userInfo,
        username: username.trim(),
        ...(response.user && response.user) // Merge any additional user data from response
      };
      
      // Update localStorage with the new username
      localStorage.setItem('user', JSON.stringify(updatedUserInfo));
      
      onComplete(updatedUserInfo);
    } catch (error) {
      console.error('Error creating username:', error);
      if (error.message.includes('401')) {
        setError('Your session has expired. Please log in again.');
      } else if (error.message.includes('409')) {
        setError('Username is already taken or invalid. Please choose a different one.');
      } else {
        setError('Failed to create username. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleUsernameChange = (e) => {
    const value = e.target.value;
    setUsername(value);
    if (error) {
      setError(''); // Clear error when user starts typing
    }
  };

  // Mobile-specific input handling
  const handleInputFocus = (e) => {
    // On mobile, ensure the input is visible when focused
    if (window.innerWidth <= 768) {
      setTimeout(() => {
        e.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
    }
  };

  const handleInputBlur = () => {
    // On mobile, prevent zoom on input focus
    if (window.innerWidth <= 768) {
      // Small delay to prevent immediate zoom
      setTimeout(() => {
        window.scrollTo(0, 0);
      }, 100);
    }
  };

  const handleBack = () => {
    if (onClose) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="username-creation-overlay">
      <div className="username-creation-container">
        {/* Back Button */}
        <button className="back-button" onClick={handleBack}>
          ← Back
        </button>
        
        {isLoading && (
          <div className="loading-overlay">
            <div className="loading-spinner"></div>
            <p>Creating your character...</p>
          </div>
        )}
        
        {/* Background decorative elements */}
        <div className="background-decorations">
          <div className="decoration-circle circle-1"></div>
          <div className="decoration-circle circle-2"></div>
          <div className="decoration-circle circle-3"></div>
          <div className="decoration-line line-1"></div>
          <div className="decoration-line line-2"></div>
        </div>

        {/* Character avatars */}
        <div className="character-avatars">
          <div className="avatar-left">
            <img src={'/images/icons/character_1.svg'} alt="Character 1" />
          </div>
          <div className="avatar-right">
            <img src={'/images/icons/character_2.svg'} alt="Character 2" />
          </div>
        </div>
        
        {/* Logo Section */}
        <div className="logo-section">
          <div className="logo-wrapper">
            <img src="/logo.svg" alt="Logo" className="logo" />
            <div className="logo-accent"></div>
          </div>
        </div>
        
        {/* Main content */}
        <div className="main-content">
          <div className="content-container">
            <h2 className="main-title">
              Create your username<br />
              
            </h2>
            
            <div className="form-container">
              {/* <label htmlFor="username" className="username-label">Username</label> */}
              <div className="input-wrapper">
                <input
                  type="text"
                  id="username"
                  value={username}
                  onChange={handleUsernameChange}
                  onFocus={handleInputFocus}
                  onBlur={handleInputBlur}
                  placeholder="Enter your username"
                  disabled={isLoading}
                  maxLength={20}
                  autoFocus
                  className="username-input"
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck="false"
                />
                <div className="input-accent"></div>
              </div>
              {error && <div className="error-message">{error}</div>}
              
              <button 
                type="submit" 
                className="next-button"
                disabled={isLoading || !username.trim()}
                onClick={handleSubmit}
              >
                <span>Next</span>
                <div className="button-shine"></div>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UsernameCreation;
